import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { Animated, TextInput as RNTextInput, StyleSheet, View, } from 'react-native';
import { triggerHapticFeedback } from 'utils/hapticFeedback';
import { getBankConnections, setOverdraft } from 'actions';
import { AppView } from 'design-system/AppView';
import Button from 'design-system/Button';
import MoneyInputField from 'design-system/MoneyInputField';
import ShakeContainer from 'design-system/ShakeContainer';
import Spacer from 'design-system/Spacer';
import Text from 'design-system/Text';
import StatusBar from 'design-system/StatusBar';
import LogoBankAccount from 'design-system/LogoBankAccount';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { KeyboardAvoidingContainerNew } from 'design-system/KeyboardAvoidingContainerNew';
import { INVISIBLE_TEXT_COLOR, rem } from 'design-system/values';
import { DEFAULT_MAX_AMOUNT, DEFAULT_MAX_INTEGER_DIGITS, formatAmountText, getInitialScale, getScale, } from 'utils/formatting';
import createStyleSheets from 'utils/createStyleSheets';
import { Sign } from 'utils/types/navigation';
import { muteAll, useFetchOld } from 'hooks';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { blur, focus, useAutoFocusInput } from 'hooks/useAutoFocusInput';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectAccountById, selectDefaultCurrency } from 'reducers/selectors';
import { isWeb } from '../../../constants';
import PositiveNegativeBalanceInput from '../components/PositiveNegativeBalanceInput';
const OVERDRAFT_MAX_LENGTH = DEFAULT_MAX_AMOUNT.toString().length;
const getInitialState = (defaultValue) => defaultValue != null
    ? formatAmountText(defaultValue.toString(), null)
    : {
        amount: 0,
        fixedText: '',
        displayAmount: '0',
        maxLength: OVERDRAFT_MAX_LENGTH,
    };
const EditOverdraftScreen = ({ navigation, route, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    useLayoutEffect(() => {
        navigation.setOptions({
            ...(isWeb && {
                title: 'Edit overdraft',
            }),
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const account = useAppSelector((store) => selectAccountById(store, route.params.id));
    const defaultCurrency = useAppSelector(selectDefaultCurrency);
    const currency = account?.nativeBalance
        ? account?.nativeBalance.currency
        : account?.currency || defaultCurrency;
    const defaultValue = account?.nativeBalance?.overdraftLimit ??
        account?.overdraftLimit ??
        undefined;
    const [state, setState] = useState(() => getInitialState(defaultValue));
    const [value, setValue] = useState(defaultValue != null ? String(defaultValue) : '');
    const [rawValue, setRawValue] = useState(defaultValue != null ? String(defaultValue) : '');
    useEffect(() => {
        if (defaultValue != null && state.amount === 0) {
            setState(getInitialState(defaultValue));
            setValue(String(defaultValue));
            setRawValue(String(defaultValue));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValue]);
    const dispatch = useAppDispatch();
    const [, , , fetch] = useFetchOld(muteAll);
    const shake = useRef(undefined);
    const textInputRef = useRef(null);
    useAutoFocusInput(textInputRef);
    const amountScale = useRef(new Animated.Value(getInitialScale(DEFAULT_MAX_AMOUNT, (defaultValue ?? 0).toString()))).current;
    const warnAboutWrongAmount = useCallback(() => {
        if (shake.current) {
            shake.current();
        }
        triggerHapticFeedback('notificationError');
        focus(textInputRef);
    }, []);
    const onChangeText = useCallback((text) => {
        // Overdraft is a whole-number amount (precision 0) — strip any decimals
        // so a pasted/auto-inserted separator can't introduce cents.
        const values = formatAmountText(text.replace(/[^0-9]/g, ''));
        if (values) {
            setState(values);
            Animated.timing(amountScale, {
                toValue: getScale(values.displayAmount),
                duration: 100,
                delay: 200,
                useNativeDriver: true,
            }).start();
        }
        else {
            warnAboutWrongAmount();
        }
    }, [amountScale, warnAboutWrongAmount]);
    const onChangeMaskedText = useCallback((next, raw) => {
        setValue(next);
        if (typeof raw !== 'undefined') {
            setRawValue(raw);
        }
    }, []);
    const onSubmit = useCallback(() => {
        blur(textInputRef);
        const amount = isWeb ? Number(rawValue) : state.amount;
        fetch(setOverdraft(route.params.id, amount ? String(amount) : undefined)).then((response) => {
            if (response)
                dispatch(getBankConnections());
        });
        if (navigation.canGoBack()) {
            navigation.goBack();
        }
        else {
            navigation.navigateDeprecated('Transactions', { id: route.params.id });
        }
    }, [dispatch, fetch, navigation, rawValue, route.params.id, state.amount]);
    const title = `What’s your pre-arranged overdraft limit for ${account?.name}?`;
    const controls = useMemo(() => <Button brand title="Save" onPress={onSubmit}/>, [onSubmit]);
    if (isWeb) {
        return (<AppView isInTab avoidHeader paddingBottom>
        <View style={styles.container}>
          <ScreenInfoHeader title={title}/>
          <View style={styles.titleContainer}>
            <PositiveNegativeBalanceInput flex onDark sign={Sign.Positive} currency={currency} precision={0} amountPositionRight onSubmitEditing={onSubmit} value={value} onChangeText={onChangeMaskedText} ref={textInputRef} maxIntegerDigits={DEFAULT_MAX_INTEGER_DIGITS}/>
          </View>
        </View>
        <View style={styles.buttonContainer}>{controls}</View>
      </AppView>);
    }
    return (<AppView avoidHeader>
      <StatusBar barStyle="dark-content" animated/>
      <KeyboardAvoidingContainerNew flexContainer>
        <View style={styles.container}>
          {account ? <LogoBankAccount size="R64" data={account}/> : null}
          <Spacer h={16}/>
          <View style={styles.titleContainer}>
            <Text TextThin-20 Secondary centered>
              {title}
            </Text>
          </View>
          <ShakeContainer style={styles.amountContainer} registerCallback={(callback) => {
            shake.current = callback;
        }} shakeOnMount={false}>
            <Animated.View style={[
            styles.amountScalableContainer,
            { transform: [{ scale: amountScale }] },
        ]}>
              <MoneyInputField currency={currency} color={colors.text.primary}>
                {state.displayAmount}
              </MoneyInputField>
            </Animated.View>
            <RNTextInput ref={textInputRef} style={styles.transparentTextInput} value={state.fixedText} allowFontScaling={false} blurOnSubmit={false} caretHidden contextMenuHidden enablesReturnKeyAutomatically keyboardType="number-pad" maxLength={state.maxLength} onChangeText={onChangeText} onSubmitEditing={onSubmit} testID="input" pointerEvents="box-only"/>
          </ShakeContainer>
        </View>
      </KeyboardAvoidingContainerNew>
      <KeyboardAvoidingContainerNew sticky>
        {controls}
      </KeyboardAvoidingContainerNew>
    </AppView>);
};
const styleSet = createStyleSheets(() => ({
    container: isWeb
        ? {
            flex: 1,
        }
        : {
            flex: 1,
            alignItems: 'center',
        },
    titleContainer: {
        marginHorizontal: rem(16),
    },
    amountContainer: {
        marginTop: rem(40),
        width: '100%',
        height: rem(120),
        alignItems: 'center',
        justifyContent: 'center',
    },
    amountScalableContainer: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        marginHorizontal: -1000,
    },
    transparentTextInput: {
        ...StyleSheet.absoluteFillObject,
        color: INVISIBLE_TEXT_COLOR,
    },
    buttonContainer: {
        paddingHorizontal: rem(16),
    },
}));
export default EditOverdraftScreen;
